/**
 * Expenses and their GST. See docs/architecture.md §5 and the expenses part
 * of `// ---- suppliers, orders, expenses` in design/project/Bar OS.dc.html.
 *
 * Australian GST is a tenth added on, so it is a **eleventh taken off** an
 * inclusive amount. Getting that backwards — dividing by ten — overstates the
 * claim by about 10%, on a number the venue puts in a BAS. Hence a named
 * function with tests rather than an inline `* 0.1`.
 */
import { toCents } from "./calc.js";
/** GST is 1/11th of a GST-inclusive amount. */
export const GST_DIVISOR = 11;
export const EXPENSE_STATUSES = ["unmatched", "matched", "paid"];
/**
 * The GST component of a GST-inclusive amount, in dollars.
 *
 * Rounded to whole cents on the cents figure, not the dollars: `$96.50` gives
 * `$8.77`, which is what the prototype's fixture shows and what the supplier's
 * invoice will say.
 *
 * This is the **default**, not the truth. Some lines are GST-free (most fresh
 * produce is) and some invoices round differently, so the stored figure is
 * editable and this only seeds it.
 */
export function gstOf(amountIncGst) {
    if (!Number.isFinite(amountIncGst))
        return 0;
    return Math.round(toCents(amountIncGst) / GST_DIVISOR) / 100;
}
/** The ex-GST amount, given an inclusive amount and its GST component. */
export function exGst(amountIncGst, gst) {
    return (toCents(amountIncGst) - toCents(gst)) / 100;
}
/**
 * Period totals. Summed in cents and converted once, so a month of expenses
 * adds up to the cent rather than to $1,283.9999999998.
 */
export function expenseTotals(rows) {
    let amount = 0;
    let gst = 0;
    let unmatched = 0;
    let paid = 0;
    for (const row of rows) {
        amount += toCents(row.amountIncGst);
        gst += toCents(row.gst);
        if (row.status === "unmatched")
            unmatched += 1;
        if (row.status === "paid")
            paid += 1;
    }
    return { count: rows.length, amountIncGst: amount / 100, gst: gst / 100, unmatched, paid };
}
//# sourceMappingURL=expenses.js.map